import type {
  SpringboardCategoriesResponseDto,
  SpringboardCategoryItemDto,
} from '../api/springboard-categories.api';
import {
  EDITABLE_SPRINGBOARD_CATEGORY_IDS,
  type EditableSpringboardCategoryId,
} from './springboard-category.constants';
import { getSpringboardTabById } from './tabs';

/** An empty field means "use the default copy", which the reader sees in their own language. */
export interface SpringboardCustomizationFormCategory {
  id: EditableSpringboardCategoryId;
  label: string;
  subtitle: string;
  description: string;
}

export interface SpringboardCustomizationFormValues {
  categories: SpringboardCustomizationFormCategory[];
}

// eslint-disable-next-line i18next/no-literal-string -- field names, not copy
const COPY_FIELDS = ['label', 'subtitle', 'description'] as const;

export const buildCustomizationFormValues = (
  response?: SpringboardCategoriesResponseDto,
): SpringboardCustomizationFormValues => {
  const overridesById = new Map(
    response?.hasCustomization
      ? (response.categories ?? []).map((category) => [category.id, category])
      : [],
  );

  return {
    categories: EDITABLE_SPRINGBOARD_CATEGORY_IDS.map((id) => {
      const override = overridesById.get(id);
      return {
        id,
        label: override?.label ?? '',
        subtitle: override?.subtitle ?? '',
        description: override?.description ?? '',
      };
    }),
  };
};

/** The label the admin set, else the category's default in the reader's language. */
export const getCategoryDisplayLabel = (id: string, storedLabel?: string): string =>
  storedLabel || getSpringboardTabById(id)?.label || id;

/**
 * Only what the admin actually changed is stored. A field that is empty, or typed identical
 * to the default in the admin's language, is sent as null so every reader gets the default
 * in theirs.
 */
export const toSpringboardCategoryOverrides = (
  categories: SpringboardCustomizationFormCategory[],
): SpringboardCategoryItemDto[] =>
  categories.map((category) => {
    const defaults = getSpringboardTabById(category.id);
    const override: SpringboardCategoryItemDto = { id: category.id };
    for (const field of COPY_FIELDS) {
      const value = category[field].trim();
      override[field] = value && value !== defaults?.[field] ? value : null;
    }
    return override;
  });
